// src/pointContextAnalyzer.js
// Analyse complète du contexte d'un point en utilisant TOUTES les données de TennisScoreEngine

/**
 * Analyse un point et retourne les événements associés
 * @param {object} point - Point de scoreEngine.points (avec toutes les métadonnées)
 * @param {object} match - Objet match avec les noms des joueurs
 * @param {object} previousPoint - Point précédent (optionnel)
 * @param {object} scoreEngine - Instance complète du ScoreEngine (pour accès aux sets)
 * @returns {string[]} Array de phrases d'événements
 */
export function analyzePointContext(point, match, previousPoint = null, scoreEngine = null) {
  if (!point || !point.scoreAvantPoint || !point.scoreApresPoint) {
    return [];
  }
  
  console.log('🔍 analyzePointContext called:', {
    matchFinished: point.matchFinished,
    scoreAfter: point.scoreApresPoint,
    setNumero: point.setNumero,
    prevSetNumero: previousPoint?.setNumero,
    winner: point.winner
  });
  
  const events = [];
  const winner = point.winner;
  const server = point.server;
  const receiver = server === 'J1' ? 'J2' : 'J1';
  
  const winnerName = getPlayerName(match, winner);
  const serverName = getPlayerName(match, server);
  const receiverName = getPlayerName(match, receiver);
  
  const scoreBefore = point.scoreAvantPoint;
  const scoreAfter = point.scoreApresPoint;
  const formatSet = point.formatSet || 'STD';
  const isNoAd = formatSet === 'NOAD' || formatSet === 'NOADT10';
  
  // ========================================
  // 1. VICTOIRE DU MATCH (À VÉRIFIER EN PREMIER !)
  // ========================================
  // Vérifier d'abord avec le flag matchFinished du point
  if (point.matchFinished === true) {
    console.log('🏆 MATCH TERMINÉ détecté via point.matchFinished');
    events.push(`🏆 Victoire de ${winnerName} !`);
    return events;
  }
  
  // Vérifier aussi si le score indique un match terminé
  if (scoreAfter && scoreAfter.includes("Match")) {
    console.log('🏆 MATCH TERMINÉ détecté via scoreAfter');
    events.push(`🏆 Victoire de ${winnerName} !`);
    return events;
  }
  
  // 🆕 NOUVELLE DÉTECTION : Compter les sets terminés dans scoreEngine
  if (scoreEngine && scoreEngine.sets) {
    const setsToWin = scoreEngine.formatMatch === 'BEST_OF_5' ? 3 : 2;
    let j1SetsTotal = 0;
    let j2SetsTotal = 0;
    
    console.log('🔍 scoreEngine.sets:', scoreEngine.sets);
    console.log('🔍 matchFormats:', scoreEngine.matchFormats || point.formatSet);
    
    scoreEngine.sets.forEach(([s1, s2], idx) => {
      // Récupérer le format de CE set (pas du point actuel)
      const setFormat = scoreEngine.matchFormats ? scoreEngine.matchFormats[idx] : 'STD';
      
      const isCompleted = (
        (s1 >= 6 && s1 - s2 >= 2) ||           // 6-x (x≤4), 7-5, 8-6...
        (s2 >= 6 && s2 - s1 >= 2) ||           // 6-x (x≤4), 7-5, 8-6...
        (s1 === 7 && s2 === 6) ||              // 7-6 (tie-break)
        (s2 === 7 && s1 === 6) ||              // 7-6 (tie-break)
        // 1-0 SEULEMENT pour super tie-break
        ((s1 === 1 && s2 === 0) && (setFormat === 'S10' || setFormat === 'T10')) ||
        ((s2 === 1 && s1 === 0) && (setFormat === 'S10' || setFormat === 'T10'))
      );
      
      console.log(`🔍 Set ${idx}: ${s1}-${s2}, format=${setFormat}, completed=${isCompleted}`);
      
      if (isCompleted) {
        if (s1 > s2) j1SetsTotal++;
        else if (s2 > s1) j2SetsTotal++;
      }
    });
    
    console.log(`🔍 Check victoire EARLY: J1=${j1SetsTotal} sets, J2=${j2SetsTotal} sets, need=${setsToWin}`);
    
    // Si un joueur a atteint le nombre de sets requis → MATCH TERMINÉ
    if (j1SetsTotal >= setsToWin || j2SetsTotal >= setsToWin) {
      console.log('🏆 MATCH TERMINÉ détecté via comptage sets (EARLY)');
      events.push(`🏆 Victoire de ${winnerName} !`);
      return events;
    }
  }
  
  // Vérifier également en comptant les sets (après que le set soit terminé)
  if (scoreEngine && previousPoint && point.setNumero > previousPoint.setNumero) {
    console.log('🔍 Changement de set détecté:', point.setNumero, '>', previousPoint.setNumero);
    
    // Un nouveau set vient de commencer, compter les sets du match
    const setsToWin = scoreEngine.formatMatch === 'BEST_OF_5' ? 3 : 2;
    
    // Compter TOUS les sets (y compris celui qui vient de se terminer)
    let j1SetsTotal = 0;
    let j2SetsTotal = 0;
    
    console.log('🔍 scoreEngine.sets:', scoreEngine.sets);
    
    scoreEngine.sets.forEach(([s1, s2], idx) => {
      // Récupérer le format de CE set
      const setFormat = scoreEngine.matchFormats ? scoreEngine.matchFormats[idx] : 'STD';
      
      const isCompleted = (
        (s1 >= 6 && s1 - s2 >= 2) ||
        (s2 >= 6 && s2 - s1 >= 2) ||
        (s1 === 7 && s2 === 6) ||
        (s2 === 7 && s1 === 6) ||
        // 1-0 SEULEMENT pour super tie-break
        ((s1 === 1 && s2 === 0) && (setFormat === 'S10' || setFormat === 'T10')) ||
        ((s2 === 1 && s1 === 0) && (setFormat === 'S10' || setFormat === 'T10'))
      );
      
      console.log(`🔍 Set ${idx}: ${s1}-${s2}, format=${setFormat}, completed=${isCompleted}`);
      
      if (isCompleted) {
        if (s1 > s2) j1SetsTotal++;
        else if (s2 > s1) j2SetsTotal++;
      }
    });
    
    console.log(`🔍 Check victoire: J1=${j1SetsTotal} sets, J2=${j2SetsTotal} sets, need=${setsToWin}`);
    
    // Si un joueur a atteint le nombre de sets requis
    if (j1SetsTotal >= setsToWin || j2SetsTotal >= setsToWin) {
      console.log('🏆 MATCH TERMINÉ détecté via comptage sets');
      events.push(`🏆 Victoire de ${winnerName} !`);
      return events;
    }
  }
  
  // ========================================
  // 2. SET GAGNÉ (détecter mais ne pas encore afficher)
  // ========================================
  // Détection fiable : setNumero a augmenté OU score de set en S10 = 1-0
  let setWon = previousPoint && point.setNumero > previousPoint.setNumero;
  
  // 🆕 Détecter aussi si le set vient d'être gagné en S10 (score passe à 1-0)
  if (!setWon && scoreEngine && scoreEngine.sets) {
    const currentSetIndex = point.setNumero - 1;
    const currentSetScore = scoreEngine.sets[currentSetIndex];
    const setFormat = (scoreEngine.matchFormats && scoreEngine.matchFormats[currentSetIndex]) || formatSet;
    
    if (currentSetScore && (setFormat === 'S10' || setFormat === 'T10')) {
      const [s1, s2] = currentSetScore;
      // Si le score est 1-0 ou 0-1, c'est que le set vient d'être gagné
      if ((s1 === 1 && s2 === 0) || (s1 === 0 && s2 === 1)) {
        setWon = true;
        console.log('🔍 Set gagné détecté via score S10: ', s1, '-', s2);
      }
    }
  }
  
  let setScoreText = '';
  
  if (setWon) {
    // Récupérer le score du set qui vient de se terminer
    const setIndex = previousPoint.setNumero - 1;
    if (scoreEngine && scoreEngine.sets && scoreEngine.sets[setIndex]) {
      const [j1Games, j2Games] = scoreEngine.sets[setIndex];
      setScoreText = ` (${j1Games}-${j2Games})`;
      
      // Afficher le score de tie-break si applicable
      if (scoreEngine.lastTieBreakScores && scoreEngine.lastTieBreakScores[setIndex]) {
        const tbScore = scoreEngine.lastTieBreakScores[setIndex];
        if (Array.isArray(tbScore)) {
          setScoreText += ` [${tbScore[0]}-${tbScore[1]}]`;
        }
      }
    }
  }
  
  // ========================================
  // 3. TIE-BREAK
  // ========================================
  // Détection : jeuNumero passe de non-0 à 0 (début tie-break)
  if (previousPoint && point.jeuNumero === 0 && previousPoint.jeuNumero !== 0) {
    if (formatSet === 'S10') {
      events.push(`🚀 Super tie-break à 10 !`);
    } else if (formatSet === 'T10') {
      events.push(`🚀 Tie-break à 10 !`);
    } else {
      events.push(`🚀 Tie-break !`);
    }
  }
  
  // ========================================
  // 4. JEU GAGNÉ (combiné avec SET si applicable)
  // ========================================
  const gameWon = isGameWon(scoreBefore, scoreAfter);
  if (gameWon && point.jeuNumero !== 0) {  // Pas en tie-break
    // Vérifier si c'est un break
    const isBreak = (winner === receiver);
    
    // Construire le message selon les cas
    if (isBreak && setWon) {
      // Break + Set
      events.push(`🔥 Break ! Jeu et Set pour ${winnerName}${setScoreText} !`);
    } else if (isBreak) {
      // Break seulement
      events.push(`🔥 Break ! Jeu pour ${winnerName} !`);
    } else if (setWon) {
      // Jeu + Set (pas break)
      events.push(`🎯 Jeu et Set pour ${winnerName}${setScoreText} !`);
    } else {
      // Jeu seulement
      events.push(`🎯 Jeu pour ${winnerName} !`);
    }
  } else if (setWon && !gameWon) {
    // Set gagné mais sans jeu (cas rare, tie-break par exemple)
    events.push(`📊 Set pour ${winnerName}${setScoreText} !`);
  }
  
  // ========================================
  // 5. BALLES DE BREAK
  // ========================================
  // Analyser d'abord les balles de match/set pour savoir si on doit afficher les balles de break
  let matchOrSetBallSaved = false;
  let hasMatchOrSetPoint = false;  // 🆕 Flag pour savoir s'il y a des balles de match/set ACTUELLEMENT
  
  // Pré-analyse rapide pour savoir si une balle de match/set a été sauvée OU existe actuellement
  if (scoreEngine && point.jeuNumero !== 0) {
    const matchPointInfo = analyzeMatchPoint(
      scoreEngine, 
      scoreBefore, scoreAfter, 
      server, winner, 
      formatSet, gameWon,
      point.setNumero
    );
    
    if (matchPointInfo.hadMatchPoint && matchPointInfo.saved) {
      matchOrSetBallSaved = true;
    }
    
    // 🆕 Vérifier si on a des balles de match MAINTENANT
    if (matchPointInfo.hasMatchPoint) {
      hasMatchOrSetPoint = true;
    }
    
    // Si pas de balle de match, vérifier les balles de set
    if (!matchOrSetBallSaved && !hasMatchOrSetPoint && scoreEngine.sets) {
      const currentSetIndex = point.setNumero - 1;
      const currentSetScore = scoreEngine.sets[currentSetIndex] || [0, 0];
      const [j1Games, j2Games] = currentSetScore;
      
      const setPointInfo = analyzeSetPoint(
        j1Games, j2Games, 
        scoreBefore, scoreAfter, 
        server, winner, 
        formatSet, gameWon
      );
      
      if (setPointInfo.hadSetPoint && setPointInfo.saved) {
        matchOrSetBallSaved = true;
      }
      
      // 🆕 Vérifier si on a des balles de set MAINTENANT
      if (setPointInfo.hasSetPoint) {
        hasMatchOrSetPoint = true;
      }
    }
  }
  
  // Maintenant afficher les balles de break SEULEMENT si :
  // - Pas en tie-break
  // - Pas de balle match/set sauvée
  // - Pas de balles match/set actuellement
  // - Jeu pas encore fini
  if (point.jeuNumero !== 0 && !matchOrSetBallSaved && !hasMatchOrSetPoint && !gameWon) {
    // Convertir score en "serveur-retourneur"
    let scoreBeforeConverted = scoreBefore;
    let scoreAfterConverted = scoreAfter;
    
    if (server === 'J2') {
      const partsBefore = scoreBefore.split('-');
      const partsAfter = scoreAfter.split('-');
      if (partsBefore.length === 2) {
        scoreBeforeConverted = `${partsBefore[1]}-${partsBefore[0]}`;
      }
      if (partsAfter.length === 2) {
        scoreAfterConverted = `${partsAfter[1]}-${partsAfter[0]}`;
      }
    }
    
    const ballsBefore = countBreakBalls(scoreBeforeConverted, isNoAd);
    const ballsAfter = countBreakBalls(scoreAfterConverted, isNoAd);
    
    console.log('🔍 Break balls:', {
      scoreBefore: scoreBeforeConverted,
      scoreAfter: scoreAfterConverted,
      ballsBefore,
      ballsAfter,
      winner,
      server
    });
    
    // Balle de break sauvée ?
    if (ballsBefore > 0 && winner === server) {
      events.push(`🛡️ Balle de break sauvée par ${serverName} !`);
    }
    
    // Nouvelles balles de break APRÈS ce point ?
    if (ballsAfter > 0) {
      const plural = ballsAfter > 1 ? 's' : '';
      events.push(`💥 ${ballsAfter} balle${plural} de break pour ${receiverName} !`);
    }
  }
  
  // ========================================
  // 6. BALLES DE SET
  // ========================================
  // Les balles de MATCH ont priorité : si c'est une balle de match, 
  // on ne montre PAS "balle de set"
  
  let hasMatchPoint = false;  // Flag pour savoir si on a des balles de match
  
  // D'abord analyser les balles de match pour savoir
  if (scoreEngine) {  // ✅ Autoriser en tie-break
    const matchPointInfo = analyzeMatchPoint(
      scoreEngine, 
      scoreBefore, scoreAfter, 
      server, winner, 
      formatSet, gameWon,
      point.setNumero
    );
    
    // Marquer si on a des balles de match
    hasMatchPoint = matchPointInfo.hasMatchPoint || matchPointInfo.hadMatchPoint;
  }
  
  // Maintenant analyser les balles de set (SAUF si c'est une balle de match)
  if (scoreEngine && scoreEngine.sets && !hasMatchPoint) {  // ✅ Autoriser en tie-break
    const currentSetIndex = point.setNumero - 1;
    const currentSetScore = scoreEngine.sets[currentSetIndex] || [0, 0];
    const [j1Games, j2Games] = currentSetScore;
    
    const setPointInfo = analyzeSetPoint(
      j1Games, j2Games, 
      scoreBefore, scoreAfter, 
      server, winner, 
      formatSet, gameWon
    );
    
    if (setPointInfo.hadSetPoint && setPointInfo.saved) {
      const saverName = getPlayerName(match, setPointInfo.saver);
      events.push(`🛡️ Balle de set sauvée par ${saverName} !`);
      // ❌ Pas de "Reste X balles" (redondant avec "X balles pour..." ci-dessous)
    }
    
    if (setPointInfo.hasSetPoint && !gameWon) {
      const plural = setPointInfo.count > 1 ? 's' : '';
      const holderName = getPlayerName(match, setPointInfo.holder);
      events.push(`🔥 ${setPointInfo.count} balle${plural} de set pour ${holderName} !`);
    }
  }
  
  // ========================================
  // 7. BALLES DE MATCH
  // ========================================
  if (scoreEngine) {  // ✅ Autoriser en tie-break
    const matchPointInfo = analyzeMatchPoint(
      scoreEngine, 
      scoreBefore, scoreAfter, 
      server, winner, 
      formatSet, gameWon,
      point.setNumero
    );
    
    if (matchPointInfo.hadMatchPoint && matchPointInfo.saved) {
      const saverName = getPlayerName(match, matchPointInfo.saver);
      events.push(`🛡️ Balle de match sauvée par ${saverName} !`);
      // ❌ Pas de "Reste X balles" (redondant avec "X balles pour..." ci-dessous)
    }
    
    if (matchPointInfo.hasMatchPoint && !gameWon) {
      const plural = matchPointInfo.count > 1 ? 's' : '';
      const holderName = getPlayerName(match, matchPointInfo.holder);
      events.push(`🏆 ${matchPointInfo.count} balle${plural} de match pour ${holderName} !`);
    }
  }
  
  return events;
}

/**
 * Analyse les balles de set
 */
function analyzeSetPoint(j1Games, j2Games, scoreBefore, scoreAfter, server, winner, formatSet, gameWon) {
  const result = {
    hadSetPoint: false,
    saved: false,
    saver: null,
    hasSetPoint: false,
    holder: null,
    count: 0,
    remaining: 0
  };
  
  console.log('🔍 analyzeSetPoint appelé:', {
    j1Games,
    j2Games,
    formatSet,
    scoreBefore,
    scoreAfter
  });
  
  // 🆕 Détecter si on est en tie-break (scores numériques comme "5-0")
  const isTieBreak = (
    scoreBefore.split('-')[0].match(/^\d+$/) && 
    parseInt(scoreBefore.split('-')[0]) >= 0 &&
    j1Games === 6 && j2Games === 6  // Seulement si 6-6 en jeux
  );
  
  // 🆕 Super tie-break : regarder le score, pas les jeux
  const isSuperTieBreak = formatSet === 'S10' || formatSet === 'T10';
  
  let leader = null;
  let leaderGames = 0;
  let trailerGames = 0;
  
  if (isTieBreak || isSuperTieBreak) {
    // En tie-break : utiliser le score du TB, pas les jeux
    const [p1, p2] = scoreAfter.split('-').map(Number);
    
    if (p1 > p2) {
      leader = 'J1';
      leaderGames = p1;
      trailerGames = p2;
    } else if (p2 > p1) {
      leader = 'J2';
      leaderGames = p2;
      trailerGames = p1;
    }
    
    console.log('🔍 TIE-BREAK - leader:', leader, 'score APRÈS:', leaderGames, '-', trailerGames);
  } else {
    // Jeu normal : utiliser les jeux
    const isJ1Leading = j1Games > j2Games;
    const isJ2Leading = j2Games > j1Games;
    leader = isJ1Leading ? 'J1' : (isJ2Leading ? 'J2' : null);
    leaderGames = leader === 'J1' ? j1Games : j2Games;
    trailerGames = leader === 'J1' ? j2Games : j1Games;
  }
  
  if (!leader) {
    console.log('🔍 Pas de leader détecté');
    return result;
  }
  
  const trailer = leader === 'J1' ? 'J2' : 'J1';
  
  let canWinSet = false;
  
  if (isSuperTieBreak) {
    // Super tie-break : peut gagner à partir de 9 points (si écart de 2)
    canWinSet = (leaderGames >= 9);
    console.log('🔍 Super TB canWinSet:', canWinSet, `(leader=${leaderGames}, trailer=${trailerGames})`);
  } else if (isTieBreak) {
    // Tie-break normal à 6-6 : peut gagner à partir de 6 points
    canWinSet = (leaderGames >= 6);
    console.log('🔍 TB normal canWinSet:', canWinSet, `(leader=${leaderGames}, trailer=${trailerGames})`);
  } else {
    // Jeu normal : peut gagner le set si :
    canWinSet = (
      (leaderGames === 5 && trailerGames <= 4) ||  // 5-4, 5-3, etc.
      (leaderGames === 6 && trailerGames === 5)     // 6-5
    );
  }
  
  if (!canWinSet) {
    console.log('🔍 canWinSet=false, sortie');
    return result;
  }
  
  // 🆕 En tie-break : pas de vérification break/non-break
  if (!isTieBreak && !isSuperTieBreak) {
    // 🆕 VÉRIFICATION SUPPLÉMENTAIRE (jeu normal uniquement)
    const breakWouldWinSet = (
      (leaderGames === 5 && trailerGames <= 4) ||
      (leaderGames === 6 && trailerGames === 5)
    );
    
    // Si c'est le trailer qui sert et que le break ne gagne PAS le set → pas de balle de set
    if (server === trailer && !breakWouldWinSet) {
      return result;
    }
  }
  
  // ========================================
  // CAS 1 : LEADER AU SERVICE (ou en tie-break)
  // ========================================
  if (server === leader || isTieBreak || isSuperTieBreak) {
    // Convertir score en serveur-retourneur (sauf en TB)
    let scoreConv = scoreBefore;
    if (!isTieBreak && !isSuperTieBreak && server === 'J2') {
      const parts = scoreBefore.split('-');
      if (parts.length === 2) {
        scoreConv = `${parts[1]}-${parts[0]}`;
      }
    }
    
    // 🆕 VÉRIFICATION CRITIQUE : Le serveur a-t-il des balles de set ?
    // Vérifier AVANT et APRÈS le point
    if (!isTieBreak && !isSuperTieBreak) {
      // Vérifier scoreAfter pour les balles de BREAK du retourneur
      let scoreAfterConv = scoreAfter;
      if (server === 'J2') {
        const parts = scoreAfter.split('-');
        if (parts.length === 2) {
          scoreAfterConv = `${parts[1]}-${parts[0]}`;
        }
      }
      
      const breakBallCountAfter = countBreakBalls(scoreAfterConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
      console.log(`🔍 Check break balls APRÈS: scoreAfter=${scoreAfter}, scoreAfterConv=${scoreAfterConv}, breakBallCount=${breakBallCountAfter}, server=${server}`);
      
      // Si APRÈS ce point le retourneur a des balles de break → PAS de balles de set
      if (breakBallCountAfter > 0) {
        console.log(`🔍 Serveur ${server} perdra ${scoreAfterConv} APRÈS ce point → balles de BREAK, pas de set`);
        return result;
      }
    }
    
    const count = countSetOrMatchBalls(scoreConv, formatSet);
    
    console.log(`🔍 TB/S10 count balles: scoreConv=${scoreConv}, formatSet=${formatSet}, count=${count}, isTB=${isTieBreak}, isSuperTB=${isSuperTieBreak}`);
    
    // 🆕 EN TIE-BREAK : Compter les balles différemment
    let actualCount = count;
    if (isTieBreak || isSuperTieBreak) {
      // En TB, le nombre de balles = écart de points du leader
      // Exemple : 9-3 → écart 6 → 6 balles (peut perdre 5 points jusqu'à 9-8, puis gagner 10-8)
      // Exemple : 6-5 → écart 1 → 1 balle (si perd → 6-6 → plus de balle)
      // Exemple : 10-8 → écart 2 → 2 balles (peut perdre 1 point jusqu'à 10-9, puis gagner 11-9)
      
      const target = isSuperTieBreak ? 10 : 7;
      const ecart = leaderGames - trailerGames;
      
      // Si le leader peut gagner directement (à target-1 ou plus)
      if (leaderGames >= target - 1 && ecart >= 1) {
        // Nombre de balles = écart
        actualCount = ecart;
        console.log(`🔍 TB balle(s) de set: leader=${leaderGames}, trailer=${trailerGames}, target=${target}, ecart=${ecart} → ${actualCount} balle(s)`);
      } else {
        actualCount = 0;
        console.log(`🔍 TB PAS de balle: leader=${leaderGames}, trailer=${trailerGames}, target=${target}, ecart=${ecart}`);
      }
    }
    
    // AVANT le point
    if (actualCount > 0) {
      result.hadSetPoint = true;
      
      // Si le leader perd le point
      if (winner !== leader && !gameWon) {
        result.saved = true;
        result.saver = winner;
        
        const scoreAfterConv = (!isTieBreak && !isSuperTieBreak && leader === 'J2')
          ? scoreAfter.split('-').reverse().join('-') 
          : scoreAfter;
        result.remaining = countSetOrMatchBalls(scoreAfterConv, formatSet);
      }
    }
    
    // APRÈS le point (nouvelles balles)
    if (!gameWon) {
      let scoreAfterConv = scoreAfter;
      if (!isTieBreak && !isSuperTieBreak && leader === 'J2') {
        const parts = scoreAfter.split('-');
        if (parts.length === 2) {
          scoreAfterConv = `${parts[1]}-${parts[0]}`;
        }
      }
      
      const countAfter = countSetOrMatchBalls(scoreAfterConv, formatSet);
      
      // 🆕 EN TIE-BREAK : Recalculer avec le score APRÈS
      let actualCountAfter = countAfter;
      if (isTieBreak || isSuperTieBreak) {
        // Parser le score APRÈS pour avoir les vrais points
        const [p1After, p2After] = scoreAfter.split('-').map(Number);
        const leaderScoreAfter = (leader === 'J1') ? p1After : p2After;
        const trailerScoreAfter = (leader === 'J1') ? p2After : p1After;
        
        const target = isSuperTieBreak ? 10 : 7;
        const ecartAfter = leaderScoreAfter - trailerScoreAfter;
        
        if (leaderScoreAfter >= target - 1 && ecartAfter >= 1) {
          // Nombre de balles = écart
          actualCountAfter = ecartAfter;
          console.log(`🔍 TB APRÈS balle(s) de set: leaderAfter=${leaderScoreAfter}, trailerAfter=${trailerScoreAfter}, ecart=${ecartAfter} → ${actualCountAfter} balle(s)`);
        } else {
          actualCountAfter = 0;
        }
      }
      
      if (actualCountAfter > 0) {
        result.hasSetPoint = true;
        result.holder = leader;
        result.count = actualCountAfter;
      }
    }
  }
  // ========================================
  // CAS 2 : TRAILER AU SERVICE (leader au retour) - JEU NORMAL UNIQUEMENT
  // ========================================
  else if (server === trailer && !isTieBreak && !isSuperTieBreak) {
    // Le leader est au retour
    let scoreConv = scoreBefore;
    if (server === 'J2') {
      const parts = scoreBefore.split('-');
      if (parts.length === 2) {
        scoreConv = `${parts[1]}-${parts[0]}`;
      }
    }
    
    const count = countBreakBalls(scoreConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
    
    if (count > 0) {
      result.hadSetPoint = true;
      
      if (winner === server && !gameWon) {
        result.saved = true;
        result.saver = winner;
        
        let scoreAfterConv = scoreAfter;
        if (server === 'J2') {
          const parts = scoreAfter.split('-');
          if (parts.length === 2) {
            scoreAfterConv = `${parts[1]}-${parts[0]}`;
          }
        }
        result.remaining = countBreakBalls(scoreAfterConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
      }
    }
    
    if (!gameWon) {
      let scoreAfterConv = scoreAfter;
      if (server === 'J2') {
        const parts = scoreAfter.split('-');
        if (parts.length === 2) {
          scoreAfterConv = `${parts[1]}-${parts[0]}`;
        }
      }
      
      const countAfter = countBreakBalls(scoreAfterConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
      if (countAfter > 0) {
        result.hasSetPoint = true;
        result.holder = leader;
        result.count = countAfter;
      }
    }
  }
  
  return result;
}

/**
 * Analyse les balles de match
 */
function analyzeMatchPoint(scoreEngine, scoreBefore, scoreAfter, server, winner, formatSet, gameWon, currentSetNum) {
  const result = {
    hadMatchPoint: false,
    saved: false,
    saver: null,
    hasMatchPoint: false,
    holder: null,
    count: 0,
    remaining: 0
  };
  
  if (!scoreEngine || !scoreEngine.sets) {
    console.log('🔍 analyzeMatchPoint: pas de scoreEngine');
    return result;
  }
  
  // Compter les sets gagnés (SEULEMENT les sets TERMINÉS)
  const setsToWin = scoreEngine.formatMatch === 'BEST_OF_5' ? 3 : 2;
  let j1SetsWon = 0;
  let j2SetsWon = 0;
  
  scoreEngine.sets.forEach(([s1, s2], index) => {
    // Un set est terminé si :
    // - Un joueur a 6+ jeux ET 2 jeux d'écart (6-4, 6-3, 6-2, 6-1, 6-0)
    // - Score 7-6 (tie-break)
    // - Score 7-5
    // - Super tie-break (1-0)
    
    const isCompleted = (
      (s1 >= 6 && s1 - s2 >= 2) ||  // J1 gagne 6-x (x≤4), 7-5, 8-6...
      (s2 >= 6 && s2 - s1 >= 2) ||  // J2 gagne 6-x, 7-5...
      (s1 === 7 && s2 === 6) ||     // J1 gagne 7-6 (TB)
      (s2 === 7 && s1 === 6) ||     // J2 gagne 7-6 (TB)
      (s1 === 1 && s2 === 0) ||     // J1 gagne super TB
      (s2 === 1 && s1 === 0)        // J2 gagne super TB
    );
    
    // ✅ Compter SEULEMENT si set terminé
    if (isCompleted) {
      if (s1 > s2) j1SetsWon++;
      else if (s2 > s1) j2SetsWon++;
    }
  });
  
  console.log(`🔍 analyzeMatchPoint: J1=${j1SetsWon} sets, J2=${j2SetsWon} sets, need=${setsToWin}`);
  
  // Balle de match si un joueur a (setsToWin - 1) sets
  const j1CanWinMatch = (j1SetsWon === setsToWin - 1);
  const j2CanWinMatch = (j2SetsWon === setsToWin - 1);
  
  console.log(`🔍 analyzeMatchPoint: J1 can win=${j1CanWinMatch}, J2 can win=${j2CanWinMatch}`);
  
  if (!j1CanWinMatch && !j2CanWinMatch) return result;
  
  const leader = j1CanWinMatch ? 'J1' : 'J2';
  const trailer = leader === 'J1' ? 'J2' : 'J1';
  
  console.log(`🔍 analyzeMatchPoint: leader=${leader}, server=${server}, trailer=${trailer}`);
  
  // Récupérer le score de jeux du set actuel
  const currentSetIndex = currentSetNum - 1;
  const currentSetScore = scoreEngine.sets[currentSetIndex] || [0, 0];
  const [j1Games, j2Games] = currentSetScore;
  
  console.log(`🔍 analyzeMatchPoint: jeux J1=${j1Games}, J2=${j2Games}, setNum=${currentSetNum}`);
  
  // 🆕 Détecter si on est en tie-break (scores numériques)
  const isTieBreak = (
    scoreBefore.split('-')[0].match(/^\d+$/) && 
    parseInt(scoreBefore.split('-')[0]) >= 0 &&
    j1Games === 6 && j2Games === 6
  );
  
  const isSuperTieBreak = formatSet === 'S10' || formatSet === 'T10';
  
  let leaderGames = leader === 'J1' ? j1Games : j2Games;
  let trailerGames = leader === 'J1' ? j2Games : j1Games;
  
  // 🆕 En tie-break : utiliser les scores du TB
  if (isTieBreak || isSuperTieBreak) {
    const [p1, p2] = scoreAfter.split('-').map(Number);
    if (p1 > p2) {
      leaderGames = p1;
      trailerGames = p2;
    } else if (p2 > p1) {
      leaderGames = p2;
      trailerGames = p1;
    }
    console.log(`🔍 analyzeMatchPoint TIE-BREAK: leader=${leaderGames}, trailer=${trailerGames}`);
  }
  
  // Peut gagner le set (et donc le match) si :
  let canWinSet = false;
  
  if (isSuperTieBreak) {
    canWinSet = (leaderGames >= 9);
  } else if (isTieBreak) {
    canWinSet = (leaderGames >= 6);
  } else {
    canWinSet = (
      (leaderGames === 5 && trailerGames <= 4) ||
      (leaderGames === 6 && trailerGames === 5)
    );
  }
  
  console.log(`🔍 analyzeMatchPoint: canWinSet=${canWinSet} (leader=${leaderGames}, trailer=${trailerGames})`);
  
  if (!canWinSet) return result;
  
  // ========================================
  // CAS 1 : LEADER AU SERVICE (ou en tie-break)
  // ========================================
  if (server === leader || isTieBreak || isSuperTieBreak) {
    let scoreConv = scoreBefore;
    if (!isTieBreak && !isSuperTieBreak && server === 'J2') {
      const parts = scoreBefore.split('-');
      if (parts.length === 2) {
        scoreConv = `${parts[1]}-${parts[0]}`;
      }
    }
    
    const count = countSetOrMatchBalls(scoreConv, formatSet);
    
    // 🆕 EN TIE-BREAK : Compter différemment
    let actualCount = count;
    if (isTieBreak || isSuperTieBreak) {
      const target = isSuperTieBreak ? 10 : 7;
      const ecart = leaderGames - trailerGames;
      
      if (leaderGames >= target - 1 && ecart >= 1) {
        actualCount = ecart;
        console.log(`🔍 analyzeMatchPoint TB balles: leader=${leaderGames}, trailer=${trailerGames}, ecart=${ecart} → ${actualCount} balle(s)`);
      } else {
        actualCount = 0;
      }
    }
    
    if (actualCount > 0) {
      result.hadMatchPoint = true;
      
      if (winner !== server && !gameWon) {
        result.saved = true;
        result.saver = winner;
        
        const scoreAfterConv = server === 'J2' 
          ? scoreAfter.split('-').reverse().join('-') 
          : scoreAfter;
        result.remaining = countSetOrMatchBalls(scoreAfterConv, formatSet);
      }
    }
    
    if (!gameWon) {
      let scoreAfterConv = scoreAfter;
      if (server === 'J2') {
        const parts = scoreAfter.split('-');
        if (parts.length === 2) {
          scoreAfterConv = `${parts[1]}-${parts[0]}`;
        }
      }
      
      const countAfter = countSetOrMatchBalls(scoreAfterConv, formatSet);
      
      // 🆕 EN TIE-BREAK : Recalculer
      let actualCountAfter = countAfter;
      if (isTieBreak || isSuperTieBreak) {
        const [p1After, p2After] = scoreAfter.split('-').map(Number);
        const leaderScoreAfter = (leader === 'J1') ? p1After : p2After;
        const trailerScoreAfter = (leader === 'J1') ? p2After : p1After;
        
        const target = isSuperTieBreak ? 10 : 7;
        const ecartAfter = leaderScoreAfter - trailerScoreAfter;
        
        if (leaderScoreAfter >= target - 1 && ecartAfter >= 1) {
          actualCountAfter = ecartAfter;
          console.log(`🔍 analyzeMatchPoint TB APRÈS: leaderAfter=${leaderScoreAfter}, trailerAfter=${trailerScoreAfter} → ${actualCountAfter} balle(s)`);
        } else {
          actualCountAfter = 0;
        }
      }
      
      if (actualCountAfter > 0) {
        result.hasMatchPoint = true;
        result.holder = leader;
        result.count = actualCountAfter;
      }
    }
  }
  // ========================================
  // CAS 2 : TRAILER AU SERVICE (leader au retour) - JEU NORMAL UNIQUEMENT
  // ========================================
  else if (server === trailer && !isTieBreak && !isSuperTieBreak) {
    // Le leader est au retour
    // Score format J1-J2, on doit convertir en serveur-retourneur
    let scoreConv = scoreBefore;
    if (server === 'J2') {
      // Server = J2 (trailer), donc J1 (leader) au retour
      // Score J1-J2 → J2-J1 (serveur-retourneur)
      const parts = scoreBefore.split('-');
      if (parts.length === 2) {
        scoreConv = `${parts[1]}-${parts[0]}`;
      }
    }
    // Si server = J1 (trailer), alors J2 (leader) au retour
    // Score J1-J2 reste J1-J2 (serveur-retourneur)
    
    // Maintenant scoreConv = serveur-retourneur
    // On compte les balles pour le RETOURNEUR (= leader)
    const count = countBreakBalls(scoreConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
    
    if (count > 0) {
      result.hadMatchPoint = true;
      
      // Si le trailer (serveur) gagne le point
      if (winner === server && !gameWon) {
        result.saved = true;
        result.saver = winner;
        
        // Calculer combien reste
        let scoreAfterConv = scoreAfter;
        if (server === 'J2') {
          const parts = scoreAfter.split('-');
          if (parts.length === 2) {
            scoreAfterConv = `${parts[1]}-${parts[0]}`;
          }
        }
        result.remaining = countBreakBalls(scoreAfterConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
      }
    }
    
    if (!gameWon) {
      let scoreAfterConv = scoreAfter;
      if (server === 'J2') {
        const parts = scoreAfter.split('-');
        if (parts.length === 2) {
          scoreAfterConv = `${parts[1]}-${parts[0]}`;
        }
      }
      
      const countAfter = countBreakBalls(scoreAfterConv, formatSet === 'NOAD' || formatSet === 'NOADT10');
      if (countAfter > 0) {
        result.hasMatchPoint = true;
        result.holder = leader;  // Le leader (retourneur)
        result.count = countAfter;
      }
    }
  }
  
  return result;
}

/**
 * Compte le nombre de balles de set/match pour le SERVEUR
 */
function countSetOrMatchBalls(scoreConverted, formatSet) {
  const isNoAd = formatSet === 'NOAD' || formatSet === 'NOADT10';
  const [serverScore] = scoreConverted.split('-');
  
  // Scores de balles pour le serveur
  const serverBallScores = ['40-0', '40-15', '40-30'];
  const hasAD = scoreConverted.includes('AD') || scoreConverted.includes('A');
  
  if (hasAD) {
    // AD-40 : serveur a avantage → 1 balle
    if (serverScore === 'AD' || serverScore === 'A') return 1;
    if (scoreConverted === 'AD-40') return 1;
    if (scoreConverted === '40-AD') return 0;  // Retourneur a avantage
  }
  
  if (isNoAd && scoreConverted === '40-40') {
    return 1;  // Point décisif
  }
  
  if (serverBallScores.includes(scoreConverted)) {
    // En No-Ad : ajouter +1 pour le point décisif à 40-40
    const noadBonus = isNoAd ? 1 : 0;
    
    if (scoreConverted === '40-0') return 3 + noadBonus;   // STD=3, NoAd=4
    if (scoreConverted === '40-15') return 2 + noadBonus;  // STD=2, NoAd=3
    if (scoreConverted === '40-30') return 1 + noadBonus;  // STD=1, NoAd=2
  }
  
  return 0;
}

/**
 * Compte le nombre de balles de break
 * Réutilise la logique de statsCalculator.js
 */
function countBreakBalls(scoreConverted, isNoAd) {
  const [, receiverScore] = scoreConverted.split('-');
  
  const breakScores = ['0-40', '15-40', '30-40'];
  const hasAD = scoreConverted.includes('AD') || scoreConverted.includes('A');
  
  if (hasAD) {
    if (receiverScore === 'AD' || receiverScore === 'A') return 1;
    if (scoreConverted === 'AD-40' || scoreConverted === '40-AD') {
      return scoreConverted === 'AD-40' ? 0 : 1;
    }
  }
  
  if (isNoAd && scoreConverted === '40-40') {
    return 1;
  }
  
  if (breakScores.includes(scoreConverted)) {
    if (scoreConverted === '0-40') return 3;
    if (scoreConverted === '15-40') return 2;
    if (scoreConverted === '30-40') return 1;
  }
  
  return 0;
}

/**
 * Récupère le nom d'un joueur
 */
function getPlayerName(match, player) {
  if (!match) return player === 'J1' ? 'Joueur 1' : 'Joueur 2';
  
  if (player === 'J1') {
    return match.prenomJoueur1 || match.joueur1Nom || 'Joueur 1';
  } else {
    return match.prenomJoueur2 || match.joueur2Nom || 'Joueur 2';
  }
}

/**
 * Détecte si un jeu est gagné
 */
function isGameWon(scoreBefore, scoreAfter) {
  const newGameScores = ["0-0", "15-0", "0-15"];
  return newGameScores.includes(scoreAfter) && !newGameScores.includes(scoreBefore);
}